import React, { useState } from 'react';
import { settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Activity, Copy, Loader2, Database, Server, Gauge, Settings, BarChart3, Globe } from 'lucide-react';
import { toast } from 'sonner';

function DiagSection({ icon, title, children }) {
  return (
    <div className="space-y-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700">{icon}{title}</h3>
      <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">{children}</div>
    </div>
  );
}

function DiagRow({ label, value, warn }) {
  return (
    <div className="flex justify-between items-center py-0.5">
      <span className="text-slate-500">{label}</span>
      <span className={warn ? 'font-medium text-amber-600' : 'font-mono text-slate-900'}>{value}</span>
    </div>
  );
}

const ART_TEXT = { privat: 'privat', cloudflare: 'Cloudflare', google: 'Google-Lastverteiler', oeffentlich: 'öffentlich', ungueltig: 'ungültig' };

function XffZeile({ eintrag }) {
  return (
    <tr className="border-t border-slate-200" data-testid={`xff-zeile-${eintrag.position_von_rechts}`}>
      <td className="py-1 pr-3 font-mono">{eintrag.position_von_rechts}</td>
      <td className="py-1 pr-3">{ART_TEXT[eintrag.art] || eintrag.art}</td>
      <td className="py-1 pr-3 font-mono">{eintrag.land || '–'}</td>
      <td className="py-1 font-mono">{eintrag.maske || '–'}</td>
    </tr>
  );
}

// Kette aus X-Forwarded-For, maskiert (nur die eigene IP der aufrufenden Person, nie gespeichert).
function XffKette({ kette, empfehlung }) {
  if (!kette.length) return null;
  return (
    <div className="pt-2" data-testid="xff-kette">
      <table className="w-full text-left text-xs">
        <thead className="text-slate-500">
          <tr><th className="py-1 pr-3 font-medium">Position (von rechts)</th><th className="py-1 pr-3 font-medium">Art</th><th className="py-1 pr-3 font-medium">Land</th><th className="py-1 font-medium">Maske</th></tr>
        </thead>
        <tbody className="text-slate-900">
          {kette.map(e => <XffZeile key={e.position_von_rechts} eintrag={e} />)}
        </tbody>
      </table>
      <p className="pt-2 text-xs text-slate-600" data-testid="xff-empfehlung">
        {empfehlung
          ? `Empfehlung: IP_KETTE_POS=${empfehlung} (rechtester Cloudflare-Eintrag)`
          : 'Keine Empfehlung: kein Cloudflare-Eintrag in der Kette.'}
      </p>
    </div>
  );
}

export function SystemDiagnostics() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runDiag = async () => {
    try {
      setLoading(true);
      setResult(null);
      const res = await settingsAPI.getSystemDiagnostics();
      setResult(res.data);
    } catch (e) {
      toast.error('Fehler bei der System-Diagnose');
    } finally {
      setLoading(false);
    }
  };

  const copyResult = () => {
    if (!result) return;
    navigator.clipboard.writeText(JSON.stringify(result, null, 2)).then(() => {
      toast.success('Diagnose kopiert');
    });
  };

  const sys = result ? result.system || {} : {};
  const dbInfo = result ? result.database || {} : {};
  const counts = result ? result.data_counts || {} : {};
  const indexes = result ? result.indexes || {} : {};
  const bench = result ? result.benchmarks_ms || {} : {};
  const cfg = result ? result.config || {} : {};
  const anfrage = result ? result.anfrage || {} : {};
  const tsByStatus = counts.timesheets_by_status || {};
  const envVars = cfg.env_vars_set || {};
  const estimated = bench.estimated_many_find_one || {};

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Activity className="h-5 w-5" />
          System-Diagnose
        </CardTitle>
        <CardDescription>Detaillierter technischer Bericht über den System-Zustand</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button onClick={runDiag} disabled={loading} data-testid="run-diagnostics-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Activity className="h-4 w-4 mr-2" />}
            System-Diagnose starten
          </Button>
          {result && (
            <Button variant="outline" onClick={copyResult} data-testid="copy-diagnostics-btn">
              <Copy className="h-4 w-4 mr-2" />
              Ergebnis kopieren
            </Button>
          )}
        </div>

        {result && (
          <div className="space-y-4 pt-2" data-testid="diagnostics-result">
            <DiagSection icon={<Server className="h-4 w-4" />} title="System">
              <DiagRow label="Server-Zeit (UTC)" value={sys.server_time_utc || '—'} />
              <DiagRow label="Python" value={sys.python_version ? sys.python_version.split(' ')[0] : '—'} />
              <DiagRow label="Plattform" value={sys.platform || '—'} />
            </DiagSection>

            <DiagSection icon={<Database className="h-4 w-4" />} title="Datenbank">
              <DiagRow label="Host" value={dbInfo.host || '—'} />
              <DiagRow label="Datenbank" value={dbInfo.db_name || '—'} />
              <DiagRow label="Ping (Ø)" value={dbInfo.ping_ms_avg != null ? dbInfo.ping_ms_avg + ' ms' : '—'} warn={dbInfo.ping_ms_avg > 50} />
            </DiagSection>

            <DiagSection icon={<BarChart3 className="h-4 w-4" />} title="Datenmengen">
              <DiagRow label="Benutzer gesamt" value={counts.users ?? '—'} />
              <DiagRow label="  davon Mitarbeiter" value={counts.mitarbeiter ?? '—'} />
              <DiagRow label="Zuordnungen gesamt" value={counts.assignments ?? '—'} />
              <DiagRow label="  davon aktiv" value={counts.assignments_active ?? '—'} />
              <DiagRow label="Stundenzettel gesamt" value={counts.timesheets ?? '—'} />
              {Object.keys(tsByStatus).length > 0 && Object.keys(tsByStatus).map(s => (
                <DiagRow key={s} label={'  Status: ' + s} value={tsByStatus[s]} />
              ))}
              <DiagRow label="Stundenzettel mit Lexware-Beleg" value={counts.timesheets_mit_lexware_beleg ?? '—'} />
              <DiagRow label="  davon wiedereröffnet (Vorbeleg)" value={counts.timesheets_mit_lexware_vorbeleg ?? '—'} />
              <DiagRow label="Stundenzettel als abgerechnet markiert" value={counts.timesheets_billing_sent ?? '—'} />
              <DiagRow label="Einstellungen" value={counts.settings ?? '—'} />
              <DiagRow label="Passwort-Resets" value={counts.password_resets ?? '—'} />
            </DiagSection>

            <DiagSection icon={<Database className="h-4 w-4" />} title="Indizes">
              {Object.keys(indexes).length > 0 && Object.keys(indexes).map(col => (
                <DiagRow key={col} label={col} value={Array.isArray(indexes[col]) ? indexes[col].join(', ') : String(indexes[col])} />
              ))}
            </DiagSection>

            <DiagSection icon={<Gauge className="h-4 w-4" />} title="Benchmarks">
              <DiagRow label="Alle Stundenzettel (aktuelles Jahr)" value={bench.load_all_timesheets_current_year != null ? bench.load_all_timesheets_current_year + ' ms' : '—'} />
              <DiagRow label="Aktive Zuordnungen laden" value={bench.load_active_assignments != null ? bench.load_active_assignments + ' ms' : '—'} />
              <DiagRow label="Mitarbeiter laden" value={bench.load_mitarbeiter != null ? bench.load_mitarbeiter + ' ms' : '—'} />
              <DiagRow label="Einzelabfrage (find_one)" value={bench.single_timesheet_find_one != null ? bench.single_timesheet_find_one + ' ms' : '—'} />
              {estimated.queries != null && (
                <>
                  <div className="border-t border-slate-200 my-1"></div>
                  <DiagRow label="Hochrechnung: Abfragen" value={estimated.queries} />
                  <DiagRow label="Hochrechnung: Gesamt" value={estimated.total_estimated_ms != null ? estimated.total_estimated_ms + ' ms' : '—'} warn={estimated.total_estimated_ms > 5000} />
                </>
              )}
            </DiagSection>

            <DiagSection icon={<Settings className="h-4 w-4" />} title="Konfiguration">
              <DiagRow label="SMTP konfiguriert" value={cfg.smtp_configured ? 'Ja' : 'Nein'} />
              <DiagRow label="Branding/Logo" value={cfg.branding_configured ? 'Ja' : 'Nein'} />
              <DiagRow label="Land-Erkennung" value={cfg.land_erkennung || '–'} />
              {Object.keys(envVars).map(v => (
                <DiagRow key={v} label={v} value={envVars[v] ? 'gesetzt' : 'FEHLT'} warn={!envVars[v]} />
              ))}
            </DiagSection>

            <DiagSection icon={<Globe className="h-4 w-4" />} title="Anfrage (Kopfzeilen)">
              {['cf-connecting-ip', 'cf-ipcountry', 'x-forwarded-for', 'x-real-ip', 'true-client-ip'].map(k => (
                <DiagRow key={k} label={k} warn={anfrage[k] !== 'ja'}
                  value={k === 'x-forwarded-for' && anfrage[k] === 'ja' ? `ja (${anfrage['x-forwarded-for_eintraege']} Einträge)` : (anfrage[k] || '–')} />
              ))}
              <DiagRow label="IP-Bremse" value={anfrage.ip_bremse || '–'} warn={!(anfrage.ip_bremse || '').startsWith('aktiv')} />
              <DiagRow label="IP-Quelle" value={anfrage.ip_quelle || '–'} warn={anfrage.ip_quelle === 'keine'} />
              <XffKette kette={anfrage.xff_kette || []} empfehlung={anfrage.empfehlung_ip_kette_pos} />
            </DiagSection>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
